import { Button } from "@validereinc/common-components";
import {
  ApprovalDecision,
  type ApprovalDecisionType,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import React from "react";
import { type ApprovalFieldType } from "./approvalFields";
import styles from "./PendingApprovalCard.module.scss";

const cx = classNames.bind(styles);

export type PendingApprovalCardDecisionType =
  | typeof ApprovalDecision.APPROVE
  | typeof ApprovalDecision.REJECT;

export type PendingApprovalCardProps = {
  fields: ApprovalFieldType[];
  messageContent?: string;
  isLoading: boolean;
  isReadOnly?: boolean;
  isResolved?: boolean;
  heading?: string;
  decision?: ApprovalDecisionType;
  fallbackMessage?: string;
  approveLabel?: string;
  rejectLabel?: string;
  onDecide: (decision: PendingApprovalCardDecisionType) => void;
};

const UNKNOWN_PAYLOAD_MESSAGE = "Approval pending (unknown payload).";
const DEFAULT_HEADING = "Approval required";
const DEFAULT_APPROVE_LABEL = "Approve";
const DEFAULT_REJECT_LABEL = "Reject";
const UNKNOWN_APPROVAL_DECISION = "unknown";
const APPROVAL_DECISION_LABELS = {
  [ApprovalDecision.APPROVE]: "Approved",
  [ApprovalDecision.REJECT]: "Rejected",
  [ApprovalDecision.ABANDON]: "Skipped",
  [UNKNOWN_APPROVAL_DECISION]: "Unknown",
} as const;

export const PendingApprovalCard = ({
  fields,
  messageContent,
  isLoading,
  isReadOnly = false,
  isResolved = false,
  heading,
  decision,
  fallbackMessage = UNKNOWN_PAYLOAD_MESSAGE,
  approveLabel = DEFAULT_APPROVE_LABEL,
  rejectLabel = DEFAULT_REJECT_LABEL,
  onDecide,
}: PendingApprovalCardProps) => {
  const resolvedHeading =
    heading ?? (messageContent?.trim() ? messageContent : DEFAULT_HEADING);
  const displayedDecision =
    decision ?? (isResolved ? UNKNOWN_APPROVAL_DECISION : null);

  return (
    <div
      className={cx("pending-approval-card", {
        "pending-approval-card--readonly": isReadOnly,
      })}
      data-testid="pending-approval-card"
      data-readonly={isReadOnly ? "true" : undefined}
    >
      <div className={cx("pending-approval-card__details")}>
        <div className={cx("pending-approval-card__heading")}>
          {resolvedHeading}
        </div>

        {fields.length > 0 ? (
          <dl className={cx("pending-approval-card__fields")}>
            {fields.map(({ label, values }, fieldIndex) => (
              <React.Fragment key={`${label}-${fieldIndex}`}>
                <dt className={cx("pending-approval-card__field-label")}>
                  {label}
                </dt>
                <dd className={cx("pending-approval-card__field-values")}>
                  {values.map((value, valueIndex) => (
                    <span
                      key={`${label}-${value}-${valueIndex}`}
                      className={cx("pending-approval-card__field-value")}
                    >
                      {value}
                    </span>
                  ))}
                </dd>
              </React.Fragment>
            ))}
          </dl>
        ) : (
          <p className={cx("pending-approval-card__fallback")}>
            {fallbackMessage}
          </p>
        )}

        {displayedDecision ? (
          <div className={cx("pending-approval-card__decision")}>
            <div className={cx("pending-approval-card__decision-row")}>
              <span
                className={cx(
                  "pending-approval-card__decision-pill",
                  `pending-approval-card__decision-pill--${displayedDecision}`
                )}
              >
                {APPROVAL_DECISION_LABELS[displayedDecision]}
              </span>
            </div>
          </div>
        ) : null}
      </div>

      {!isReadOnly && !isResolved && !decision ? (
        <div className={cx("pending-approval-card__actions")}>
          <Button
            variant="primary"
            size="small"
            isLoading={isLoading}
            disabled={isLoading}
            onClick={() => {
              onDecide(ApprovalDecision.APPROVE);
            }}
          >
            {approveLabel}
          </Button>
          <Button
            variant="outline"
            size="small"
            isLoading={isLoading}
            disabled={isLoading}
            onClick={() => {
              onDecide(ApprovalDecision.REJECT);
            }}
          >
            {rejectLabel}
          </Button>
        </div>
      ) : null}
    </div>
  );
};
